import { useContext, useEffect } from "react";
import {
  ModalBody,
  FormGroup,
  Card,
  Modal,
  Form,
  Badge,
  Alert,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import ErrorMessage from "../../../helpers/ErrorMessage/ErrorMessage";
import Button from "./../../../components/Button/Button";
import Icon from "../../../components/Icon/Icon";
import AvatarCropper from "../../../components/AvatarCropper/AvatarCropper";
import PageHeader from "../../../components/PageHeader/PageHeader";
import VerifiedBadge from "./../../../components/VerifiedBadge/VerifiedBadge";
import DateTimePicker from "../../../components/DateTimePicker/DateTimePicker";
import Currency, {
  ConvertCurrency,
} from "./../../../helpers/Currency/Currency";
import { Block } from "./../../../components/Block/Block";
import { Col, Row } from "./../../../components/Grid/Grid";
import { convertRoleToText, convertDate } from "./../../../utils/index";
import {
  changePasswordValidation,
  editUserValidation,
  verifyEmailOTPValidation,
  verifyPhoneNumberOTPValidation,
} from "../../../utils/validation/validation";
import { profileUserContext } from "../../../context/UserContext/ProfileUserContext/ProfileUserContext";
import { hostname } from "../../../services/configServices/config";
import { setUserAction } from "../../../redux/actions/userActions/userActions";
import { convertCategoryToText, categoryItems } from "../../../utils/enum";

const ProfileUser = () => {
  const dispatch = useDispatch();

  const auth = useSelector((state) => state.auth);
  const user = useSelector((state) => state.user);
  const loader = useSelector((state) => state.loader);

  const {
    isShowEditModal,
    setIsShowEditModal,
    setIsShowPasswordModal,
    handleEditUserInfo,
    handleSendEmailOTP,
    handleSendPhoneNumberOTP,
    handleUploadFile,
  } = useContext(profileUserContext);

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editUserValidation),
  });

  useEffect(() => {
    if (!isEmpty(user)) {
      setValue("avatar", user?.avatar);
      setValue("firstName", user?.firstName);
      setValue("lastName", user?.lastName);
      setValue("phoneNumber", user?.phoneNumber);
      setValue("code", user?.code);
      setValue("birthDate", user?.birthDate);
      setValue("post", user?.post);
      setValue("numberOfCovered", user?.numberOfCovered);
      setValue("email", user?.email);
    }
  }, [user]);

  useEffect(() => {
    dispatch(setUserAction());
  }, []);

  return (
    <>
      <SiteTitle title="حساب کاربری" />

      <PageHeader
        title="حساب کاربری"
        description="اطلاعات حساب کاربری رفاهی تو، برای تغییر کلمه عبور و یا اطلاعات حساب کاربری خود میتوانید از بخش مربوطه اقدام نمایید"
      />

      <Card>
        <div className="card-inner card-inner-lg">
          <Block>
            {!isEmpty(user) && (
              <>
                {!user?.phoneNumberVerified && (
                  <Alert color="danger">
                    شماره موبایل شما تایید نشده است لطفا نسبت به تایید شماره
                    موبایل خود اقدام نمایید{" "}
                    <Button
                      loading={loader}
                      onClick={handleSendPhoneNumberOTP}
                      color="success"
                      className="ml-2"
                    >
                      تایید شماره موبایل
                    </Button>
                  </Alert>
                )}
                {!user?.emailVerified && (
                  <Alert color="danger">
                    پست الکترونیک شما تایید نشده است لطفا نسبت به تایید پست
                    الکترونیک خود اقدام نمایید{" "}
                    <Button
                      loading={loader}
                      onClick={handleSendEmailOTP}
                      color="success"
                      className="ml-2"
                    >
                      تایید پست الکترونیک
                    </Button>
                  </Alert>
                )}
              </>
            )}
            {user.avatar && (
              <div className="d-flex justify-content-center align-items-center mb-3">
                <div
                  className="p-1 rounded-circle d-flex justify-content-center align-items-center"
                  style={{
                    width: 150,
                    height: 150,
                    border: "4px solid #ddd",
                  }}
                >
                  <img
                    className="img-fluid rounded-circle"
                    alt=""
                    src={`${hostname}${user.avatar}`}
                  />
                </div>
              </div>
            )}
            <div className="nk-data data-list">
              <div className="data-head">
                <h6 className="overline-title">اطلاعات</h6>
              </div>
              <div
                className="data-item"
                onClick={() => setIsShowEditModal(true)}
              >
                <div className="data-col">
                  <span className="data-label">نام</span>
                  <span className="data-value">{user.firstName}</span>
                </div>
                <div className="data-col data-col-end">
                  <span className="data-more">
                    <Icon name="forward-ios" />
                  </span>
                </div>
              </div>
              <div
                className="data-item"
                onClick={() => setIsShowEditModal(true)}
              >
                <div className="data-col">
                  <span className="data-label">نام خانوادگی</span>
                  <span className="data-value">{user.lastName}</span>
                </div>
                <div className="data-col data-col-end">
                  <span className="data-more">
                    <Icon name="forward-ios" />
                  </span>
                </div>
              </div>
              <div
                className="data-item"
                onClick={() => setIsShowEditModal(true)}
              >
                <div className="data-col">
                  <span className="data-label">
                    پست الکترونیک{" "}
                    <VerifiedBadge isVerified={user?.emailVerified} />
                  </span>
                  <span className="data-value">{user.email}</span>
                </div>
                <div className="data-col data-col-end">
                  <span className="data-more">
                    <Icon name="forward-ios" />
                  </span>
                </div>
              </div>
              <div
                className="data-item"
                onClick={() => setIsShowEditModal(true)}
              >
                <div className="data-col">
                  <span className="data-label">
                    شماره موبایل{" "}
                    <VerifiedBadge isVerified={user?.phoneNumberVerified} />
                  </span>
                  <span className="data-value">{user.phoneNumber}</span>
                </div>
                <div className="data-col data-col-end">
                  <span className="data-more">
                    <Icon name="forward-ios" />
                  </span>
                </div>
              </div>
              {user.code && (
                <div
                  className="data-item"
                  onClick={() => setIsShowEditModal(true)}
                >
                  <div className="data-col">
                    <span className="data-label">کد</span>
                    <span className="data-value">{user.code}</span>
                  </div>
                  <div className="data-col data-col-end">
                    <span className="data-more">
                      <Icon name="forward-ios" />
                    </span>
                  </div>
                </div>
              )}
              {user.birthDate && (
                <div
                  className="data-item"
                  onClick={() => setIsShowEditModal(true)}
                >
                  <div className="data-col">
                    <span className="data-label">تاریخ تولد</span>
                    <span className="data-value">
                      {convertDate(user.birthDate)}
                    </span>
                  </div>
                  <div className="data-col data-col-end">
                    <span className="data-more">
                      <Icon name="forward-ios" />
                    </span>
                  </div>
                </div>
              )}
              {user.post && (
                <div
                  className="data-item"
                  onClick={() => setIsShowEditModal(true)}
                >
                  <div className="data-col">
                    <span className="data-label">سمت</span>
                    <span className="data-value">{user.post}</span>
                  </div>
                  <div className="data-col data-col-end">
                    <span className="data-more">
                      <Icon name="forward-ios" />
                    </span>
                  </div>
                </div>
              )}
              {!!user.numberOfCovered && (
                <div className="data-item">
                  <div className="data-col">
                    <span className="data-label">تعداد افراد تحت پوشش</span>
                    <span className="data-value">{user.numberOfCovered}</span>
                  </div>
                  <div className="data-col data-col-end">
                    <span className="data-more disable">
                      <Icon name="lock-alt" />
                    </span>
                  </div>
                </div>
              )}
              <div className="data-item">
                <div className="data-col">
                  <span className="data-label">کد ملی</span>
                  <span className="data-value">{user.nationalNumber}</span>
                </div>
                <div className="data-col data-col-end">
                  <span className="data-more disable">
                    <Icon name="lock-alt" />
                  </span>
                </div>
              </div>
              <div className="data-item">
                <div className="data-col">
                  <span className="data-label">نقش</span>
                  <span className="data-value">
                    {convertRoleToText(user.role)}
                  </span>
                </div>
                <div className="data-col data-col-end">
                  <span className="data-more disable">
                    <Icon name="lock-alt" />
                  </span>
                </div>
              </div>
              <div className="data-item">
                <div className="data-col">
                  <span className="data-label">تاریخ ثبت نام</span>
                  <span className="data-value">
                    {convertDate(user.createdAt)}
                  </span>
                </div>
                <div className="data-col data-col-end">
                  <span className="data-more disable">
                    <Icon name="lock-alt" />
                  </span>
                </div>
              </div>
            </div>
            {!auth.isAdmin && !isEmpty(user.wallet) && (
              <div className="nk-data data-list">
                <div className="data-head">
                  <h6 className="overline-title">موجودی کیف پول</h6>
                </div>

                {categoryItems.map((item) => (
                  <div key={item} className="data-item">
                    <div className="data-col">
                      <span className="data-label">
                        {convertCategoryToText(item)}
                      </span>
                      <span className="data-value">
                        <ConvertCurrency price={user.wallet[item]} />{" "}
                        <Currency />
                      </span>
                    </div>
                    <div className="data-col data-col-end">
                      <span className="data-more disable">
                        <Icon name="lock-alt" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="nk-data data-list">
              <div className="data-head">
                <h6 className="overline-title">تنظیمات</h6>
              </div>
              <div className="data-item">
                <div className="data-col">
                  <span className="data-label">کلمه عبور</span>
                  <span className="data-value">**********</span>
                </div>
                <div className="data-col data-col-end">
                  <button
                    onClick={() => setIsShowPasswordModal(true)}
                    className="link link-primary"
                  >
                    تغییر کلمه عبور
                  </button>
                </div>
              </div>
            </div>
          </Block>

          <Modal
            isOpen={isShowEditModal}
            className="modal-dialog-centered"
            size="lg"
            toggle={() => setIsShowEditModal(false)}
          >
            <ModalBody>
              <span
                type="button"
                onClick={() => setIsShowEditModal(false)}
                className="close"
              >
                <Icon name="cross-sm" />
              </span>
              <div className="p-2">
                <h5 className="title">ویرایش پروفایل</h5>
                <br />

                <Form
                  className="is-alter"
                  onSubmit={handleSubmit(handleEditUserInfo)}
                >
                  <AvatarCropper
                    onRemove={() => setValue("avatar", "")}
                    value={watch("avatar")}
                    onChange={(file) =>
                      handleUploadFile(file, (e) => setValue("avatar", e))
                    }
                  />

                  <Row className="gy-4">
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="firstName">
                          نام
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            placeholder="نام خود را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("firstName")}
                          />
                          <ErrorMessage message={errors.firstName?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="lastName">
                          نام خانوادگی
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            placeholder="نام خانوادگی خود را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("lastName")}
                          />
                          <ErrorMessage message={errors.lastName?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="phoneNumber">
                          شماره موبایل{" "}
                          <VerifiedBadge
                            isVerified={user?.phoneNumberVerified}
                          />
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            placeholder="شماره موبایل خود را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("phoneNumber")}
                          />
                          <ErrorMessage message={errors.phoneNumber?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="code">
                          کد
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            placeholder="کد را وارد نمایید"
                            className="form-control-lg form-control"
                            disabled
                            {...register("code")}
                          />
                          <ErrorMessage message={errors.code?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="birthDate">
                          تاریخ تولد
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            render={({
                              field: { onChange, value, onBlur },
                            }) => (
                              <DateTimePicker
                                placeholder="تاریخ تولد را وارد نمایید"
                                initValue={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                className="form-control-lg form-control"
                              />
                            )}
                            name="birthDate"
                          />
                          <ErrorMessage message={errors.birthDate?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="post">
                          سمت
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            placeholder="سمت را وارد نمایید"
                            className="form-control-lg form-control"
                            disabled
                            {...register("post")}
                          />
                          <ErrorMessage message={errors.post?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="numberOfCovered">
                          تعداد افراد تحت پوشش
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            placeholder="تعداد افراد تحت پوشش را وارد نمایید"
                            className="form-control-lg form-control"
                            disabled
                            {...register("numberOfCovered")}
                          />
                          <ErrorMessage
                            message={errors.numberOfCovered?.message}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="email">
                          پست الکترونیک{" "}
                          <VerifiedBadge isVerified={user?.emailVerified} />
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="email"
                            placeholder="پست الکترونیک خود را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("email")}
                          />
                          <ErrorMessage message={errors.email?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col size="12">
                      <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                        <li>
                          <Button
                            color="primary"
                            size="lg"
                            type="submit"
                            loading={loader}
                          >
                            ویرایش پروفایل
                          </Button>
                        </li>
                        <li>
                          <button
                            type="button"
                            onClick={() => setIsShowEditModal(false)}
                            className="link link-light"
                          >
                            انصراف
                          </button>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </Form>
              </div>
            </ModalBody>
          </Modal>

          <ChangePassword />
          <Verified />
        </div>
      </Card>
    </>
  );
};

export default ProfileUser;

const ChangePassword = () => {
  const loader = useSelector((state) => state.loader);

  const { isShowPasswordModal, setIsShowPasswordModal, handleChangePassword } =
    useContext(profileUserContext);

  // Validation
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(changePasswordValidation),
  });

  return (
    <Modal
      isOpen={isShowPasswordModal}
      className="modal-dialog-centered"
      size="md"
      toggle={() => setIsShowPasswordModal(false)}
    >
      <ModalBody>
        <span
          type="button"
          onClick={() => setIsShowPasswordModal(false)}
          className="close"
        >
          <Icon name="cross-sm" />
        </span>
        <div className="p-2">
          <h5 className="title">ویرایش کلمه عبور</h5>
          <br />
          <Form
            className="is-alter"
            onSubmit={handleSubmit(handleChangePassword)}
          >
            <FormGroup>
              <label className="form-label" htmlFor="password">
                کلمه عبور <Badge color="info">فعلی</Badge>
              </label>
              <div className="form-control-wrap">
                <input
                  type="password"
                  placeholder="کلمه عبور خود را وارد نمایید"
                  className="form-control-lg form-control"
                  {...register("password")}
                />
                <ErrorMessage message={errors.password?.message} />
              </div>
            </FormGroup>
            <FormGroup>
              <label className="form-label" htmlFor="newPassword">
                کلمه عبور <Badge color="primary">جدید</Badge>
              </label>
              <div className="form-control-wrap">
                <input
                  type="password"
                  placeholder="کلمه عبور خود را وارد نمایید"
                  className="form-control-lg form-control"
                  {...register("newPassword")}
                />
                <ErrorMessage message={errors.newPassword?.message} />
              </div>
            </FormGroup>
            <FormGroup>
              <label className="form-label" htmlFor="confirmPassword">
                تکرار کلمه عبور <Badge color="primary">جدید</Badge>
              </label>
              <div className="form-control-wrap">
                <input
                  type="password"
                  placeholder="تکرار کلمه عبور را وارد نمایید"
                  className="form-control-lg form-control"
                  {...register("confirmPassword")}
                />
                <ErrorMessage message={errors.confirmPassword?.message} />
              </div>
            </FormGroup>
            <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
              <li>
                <Button
                  color="primary"
                  size="lg"
                  type="submit"
                  loading={loader}
                >
                  ویرایش کلمه عبور
                </Button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setIsShowPasswordModal(false)}
                  className="link link-light"
                >
                  انصراف
                </button>
              </li>
            </ul>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};

const Verified = () => {
  const loader = useSelector((state) => state.loader);
  const user = useSelector((state) => state.user);

  const {
    otpType,
    setOtpType,
    handleVerifyPhoneNumberOTP,
    handleVerifyEmailOTP,
  } = useContext(profileUserContext);

  const isEmail = otpType === "email";

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      isEmail ? verifyEmailOTPValidation : verifyPhoneNumberOTPValidation
    ),
  });

  useEffect(() => {
    if (!isEmpty(user)) {
      if (isEmail) setValue("email", user.email);
      else setValue("phoneNumber", user.phoneNumber);
    }
    setValue("otp", "");
  }, [user, otpType]);

  return (
    <Modal
      isOpen={!!otpType}
      className="modal-dialog-centered"
      size="sm"
      toggle={() => setOtpType("")}
    >
      <ModalBody>
        <span type="button" onClick={() => setOtpType("")} className="close">
          <Icon name="cross-sm" />
        </span>
        <div className="p-2">
          <h5 className="title">
            تایید {isEmail ? "پست الکترونیک" : "شماره موبایل"}
          </h5>
          <br />
          <Form
            className="is-alter"
            onSubmit={handleSubmit(
              isEmail ? handleVerifyEmailOTP : handleVerifyPhoneNumberOTP
            )}
          >
            <FormGroup>
              <div className="form-label-group">
                <label className="form-label" htmlFor="otp">
                  کد تایید
                </label>
              </div>
              <div className="form-control-wrap">
                <input
                  type="number"
                  placeholder="کد تایید خود را وارد نمایید"
                  className="form-control-lg form-control"
                  {...register("otp")}
                />

                <ErrorMessage message={errors.otp?.message} />
              </div>
            </FormGroup>

            <p className="text-center">
              زمان باقی مانده :{" "}
              <Badge
                id="otpCountdown"
                className="badge-dim font-weight-bold"
                color="primary"
              >
                120
              </Badge>{" "}
              ثانیه
            </p>
            <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
              <li>
                <Button
                  color="primary"
                  size="lg"
                  type="submit"
                  loading={loader}
                >
                  تایید {isEmail ? "پست الکترونیک" : "شماره موبایل"}
                </Button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setOtpType("")}
                  className="link link-light"
                >
                  انصراف
                </button>
              </li>
            </ul>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};
